// Defect detail (Session AAQ Loop 4). The compliance defect tiles and the Defects list now link here
// to a dedicated record view: the full defect, its lift, the advisory AI triage, any RIDDOR advisory,
// and the actions that live on a defect -- resolve/reopen, regenerate triage, and (admin) delete.
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError, type DefectDetail } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { serviceRecordHref } from "../../lib/serviceRecordLink";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { RecordHeader } from "../../components/RecordHeader";
import { StatusPill } from "../../components/StatusPill";
import { statusEntry } from "../../lib/statusRegistry";
import { formatDate } from "../../lib/format";
import { RiddorBanner } from "./DefectsPage";
import { RepairEstimatesPanel } from "./RepairEstimatesPanel";
import { RaiseWorkMenu, type RaiseWorkOption } from "./RaiseWorkMenu";

export function DefectDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Session ASM (W-12): raising work is an office action (admin and office), like every flow it opens.
  const canRaise = user?.role === "admin" || user?.role === "office";

  const [defect, setDefect] = useState<DefectDetail | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Session ANW: the office can regrade severity at any time, open or resolved. A local draft
  // choice so the select can differ from the stored value before the office confirms; it
  // re-syncs whenever a fresh defect loads (a new id, or the value the last save actually set).
  const [severityChoice, setSeverityChoice] = useState<"advisory" | "immediate">("advisory");
  const [severityBusy, setSeverityBusy] = useState(false);

  const load = useCallback(async () => {
    setState("loading");
    try {
      setDefect(await api.defects.get(id));
      setState("ready");
    } catch (err) {
      setState(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Keep the draft select in step with the loaded/saved severity, without fighting the office's
  // own in-progress choice: only resync when the STORED value itself changes (initial load, or
  // after a save actually lands), never on an unrelated re-render.
  useEffect(() => {
    if (defect) setSeverityChoice(defect.severity);
  }, [defect?.severity]);

  async function saveSeverity() {
    setSeverityBusy(true);
    setError(null);
    try {
      const updated = await api.defects.regradeSeverity(id, severityChoice);
      setDefect((d) => (d ? { ...d, ...updated } : d));
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not change the severity.");
    } finally {
      setSeverityBusy(false);
    }
  }

  async function toggleResolved(next: "resolved" | "open") {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.defects.resolve(id, next);
      setDefect((d) => (d ? { ...d, ...updated } : d));
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not update the defect.");
    } finally {
      setBusy(false);
    }
  }

  async function regenerate() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.defects.regenerateTriage(id);
      setDefect((d) => (d ? { ...d, aiTriage: res.aiTriage } : d));
      if (!res.regenerated) setError("AI triage is not configured, so the existing summary was kept.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not regenerate the triage.");
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.defects.remove(id);
      navigate("/office/defects");
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message || err.code : "Could not delete the defect.");
      setDeleteBusy(false);
    }
  }

  if (state === "loading") return <Loader shape="panel" className="p-6" />;
  if (state === "notfound") return <p className="p-6 text-center text-ele-body">That defect could not be found.</p>;
  if (state === "error" || !defect) return <p role="alert" className="p-6 text-center text-ele-strong">Could not load this defect. Please try again.</p>;

  const d = defect;
  const resolved = d.status === "resolved";
  // Mirror the list's delete rule: admin only, and not once actioned (notification drafted, remedial
  // job raised, or a RIDDOR report recorded by the Director).
  const deletable = isAdmin && !d.notificationDrafted && !d.remedialJobCreated && !(d.riddorFlag != null && d.riddorFlag.status === "reported_by_director");
  // Session ASM (W-12): what "Raise work" offers from a defect. A planned repair only while the defect is open and has
  // no repair job raised to fix it yet (the server refuses a second one: remedialLink.ts), and a call-out whenever
  // the lift's customer is known (the Log call-out form cascades from the customer). No "Start a quote": a defect is
  // priced with "Quote this" (the repair estimate) further down, and a maintenance quote is not a defect's work.
  const raiseOptions: RaiseWorkOption[] = [
    ...(d.customerId ? (["callout"] as const) : []),
    ...(!resolved && !d.remedialJobCreated ? (["repair"] as const) : []),
  ];

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <RecordHeader
        className="mb-4"
        title={d.eleNumber}
        backTo="/office/defects"
        backLabel="Back to defects"
        status={
          <>
            {/* The page's own short words ("Immediate" / "Advisory", as the select below says them); the
                tone and the solid fill are the registry's defectSeverity entry. */}
            <StatusPill
              word={d.severity === "immediate" ? "Immediate" : "Advisory"}
              tone={statusEntry("defectSeverity", d.severity).tone}
              variant="solid"
            />
            <StatusPill domain="defectStatus" status={d.status} />
            {d.siteId ? (
              <Link to={`/office/sites/${encodeURIComponent(d.siteId)}`} className="text-sm text-ele-green underline-offset-2 hover:underline">
                {d.siteName}
              </Link>
            ) : (
              <span className="text-sm text-ele-body">{d.siteName}</span>
            )}
          </>
        }
        actions={
          canRaise && raiseOptions.length > 0 ? (
            <RaiseWorkMenu
              options={raiseOptions}
              context={{
                assetId: d.assetId,
                siteId: d.siteId,
                siteName: d.siteName,
                customerId: d.customerId ?? null,
                customerName: d.customerName ?? null,
                defectId: d.id,
                defectDescription: d.description,
              }}
              onRaised={() => void load()}
            />
          ) : undefined
        }
      />

      {/* Session ANW: the office can correct a mis-graded defect at any time, open or resolved.
          Changing this and confirming re-runs the same follow-up a job completion already runs
          (a Category A client notice, or a Category B remedial job) for whichever severity is
          now current. A downgrade from Immediate never retracts an already-drafted notice --
          that stays a human decision at the office's own Gmail drafts. */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-ele border border-ele-purple-accent bg-ele-surface px-4 py-3">
        <label htmlFor="defect-severity" className="font-menu text-xs uppercase tracking-wide text-ele-body">
          Severity
        </label>
        <select
          id="defect-severity"
          value={severityChoice}
          disabled={severityBusy}
          onChange={(e) => setSeverityChoice(e.target.value as "advisory" | "immediate")}
          className="rounded-ele border border-ele-hairline bg-ele-bg px-2 py-1 text-sm text-ele-strong disabled:opacity-50"
        >
          <option value="advisory">Advisory</option>
          <option value="immediate">Immediate</option>
        </select>
        {severityChoice !== d.severity && (
          <ConfirmButton
            onConfirm={saveSeverity}
            busy={severityBusy}
            busyLabel="Saving..."
            label={severityChoice === "immediate" ? "Change to Immediate" : "Change to Advisory"}
            confirmLabel={
              severityChoice === "immediate"
                ? "Confirm: drafts a client notice"
                : "Confirm change"
            }
            className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
          />
        )}
      </div>

      {/* The real RIDDOR banner with its office actions (Mark reviewed / Dismiss / Mark reported by
          Director, or Reopen once actioned) rather than a stub pointing back to the list. Shared with
          the Defects list so both surfaces act on the flag the same way. */}
      {d.riddorFlag && (
        <div className="mb-4">
          <RiddorBanner flag={d.riddorFlag} onUpdated={() => void load()} />
        </div>
      )}

      <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
        <h2 className="mb-2 font-heading text-lg text-ele-strong">What was found</h2>
        <p className="whitespace-pre-wrap text-sm text-ele-body">{d.description}</p>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-ele-body">
          <span>Raised {d.createdAt ? formatDate(d.createdAt) : "Not set"} ({d.ageDays} {d.ageDays === 1 ? "day" : "days"} ago)</span>
          {resolved && <span>Resolved {d.resolvedAt ? formatDate(d.resolvedAt) : "Not set"}</span>}
          <Link to={`/office/lifts/${encodeURIComponent(d.assetId)}`} className="text-ele-green hover:underline">
            View lift {d.eleNumber}
          </Link>
          {/* Session ADF: link straight to the remedial repair job's service record when it has one
              (a completed visit), so the office lands on the record's panel, not the bare list. Until
              the remedial is completed there is no record, so a plain note stands in for the link. */}
          {d.remedialJobId &&
            (d.remedialJobHasSheet ? (
              <Link to={serviceRecordHref(d.remedialJobId, "repair")} className="text-ele-green hover:underline">
                View remedial record
              </Link>
            ) : (
              <span className="text-ele-body opacity-70">Repair job raised (awaiting completion)</span>
            ))}
        </div>
      </section>

      {/* Session ANU: the photos the engineer attached when they flagged this from their own job
          screen. Empty for every defect raised any other way, so the section simply is not there for
          one the office logged or a service sheet raised -- this is not a badge marking out an
          "engineer-flagged" defect, it is the evidence showing when there is any. Rendered through
          the existing office photo proxy (api.jobs.photoUrl), never a raw Drive URL, exactly as the
          job sheet's own photos already are. */}
      {d.photos.length > 0 && (
        <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
          <h2 className="mb-3 font-heading text-lg text-ele-strong">
            {d.photos.length === 1 ? "Photo from the engineer" : `${d.photos.length} photos from the engineer`}
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {d.photos.map((p) => (
              <a
                key={`${p.jobId}-${p.index}`}
                href={api.jobs.photoUrl(p.jobId, p.index)}
                target="_blank"
                rel="noreferrer"
                className="block aspect-square overflow-hidden rounded-ele border border-ele-hairline"
              >
                <img src={api.jobs.photoUrl(p.jobId, p.index)} alt="Defect photo taken on site" className="h-full w-full object-cover" />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Session ARL (W-9): "Quote this". The price of the repair, recorded before the customer has
          agreed it, starting from what was found. Separate from the remedial JOB above: the estimate
          is the price, the job is the work, and approving an estimate books nothing. */}
      <RepairEstimatesPanel defectId={d.id} defaultDescription={d.description} className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5" />

      <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
        <div className="mb-2 flex items-center justify-between gap-3">
          <h2 className="font-heading text-lg text-ele-strong">AI triage (advisory)</h2>
          <button onClick={regenerate} disabled={busy} className="rounded-ele border border-ele-blue px-3 py-1 font-menu text-xs tracking-wide text-ele-blue disabled:opacity-50">
            {busy ? "..." : "Regenerate"}
          </button>
        </div>
        {d.aiTriage ? (
          <p className="whitespace-pre-wrap text-sm text-ele-body">{d.aiTriage}</p>
        ) : (
          <p className="text-sm text-ele-body opacity-70">No triage summary yet. Use Regenerate to produce one (advisory only).</p>
        )}
      </section>

      {/* Session ARK (W-10): the remedial repair job is complete, so offer the decision it was raised
          for. Derived live from the job on every load, so the offer stays here for as long as the
          defect is open, long after the approval that first made it. It is an offer only: the button
          below is the page's ordinary Mark resolved, and leaving the defect open is a valid answer. */}
      {!resolved && d.remedialJobComplete && (
        <p role="status" className="mb-3 rounded-ele border border-ele-purple-accent bg-ele-surface px-4 py-3 text-sm text-ele-body">
          The repair job raised to fix this defect has been completed and approved. If the work fixed it, mark it resolved below. If not, leave it open.
        </p>
      )}

      {/* Session AVV (W9): the formal quote raised for this defect's remedial work, read live from the quote. */}
      {d.linkedQuote && (
        <p role="status" data-testid="defect-linked-quote" className="mb-3 rounded-ele border border-ele-purple-accent bg-ele-surface px-4 py-3 text-sm text-ele-body">
          Quote raised for this work:{" "}
          <Link to={`/office/quotes/${encodeURIComponent(d.linkedQuote.id)}`} className="text-ele-green underline-offset-2 hover:underline">
            {d.linkedQuote.reference}
          </Link>{" "}
          ({d.linkedQuote.status.replace(/_/g, " ")})
          {(d.linkedQuoteCount ?? 0) > 1 ? `. ${d.linkedQuoteCount} quotes are linked to this defect; this is the newest one still open.` : "."}
        </p>
      )}

      {error && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        {resolved ? (
          <button onClick={() => toggleResolved("open")} disabled={busy} className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong disabled:opacity-50">
            Reopen defect
          </button>
        ) : (
          <Button onClick={() => toggleResolved("resolved")} disabled={busy}>
            Mark resolved
          </Button>
        )}
        {deletable && (
          <Button
            variant="destructive"
            onClick={() => {
              setDeleteError(null);
              setDeleteOpen(true);
            }}
          >
            Delete defect
          </Button>
        )}
      </div>

      <ConfirmDeleteModal
        open={deleteOpen}
        title="Delete defect?"
        message="This permanently removes the defect. Only an untouched defect (no client notification, repair job or recorded RIDDOR report) can be deleted. This cannot be undone."
        confirmLabel="Delete defect"
        busy={deleteBusy}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => {
          if (deleteBusy) return;
          setDeleteOpen(false);
          setDeleteError(null);
        }}
      />
    </div>
  );
}
